import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2FormFieldLibraryMutationResponse,
  AdminV2FormFieldLibraryResponse,
  AdminV2GrowthFormDetailResponse,
  AdminV2GrowthFormFieldType,
  AdminV2GrowthFormsListResponse,
  AdminV2GrowthFormStatus,
  JsonValue as AdminV2JsonValue,
} from "@/types/adminV2";

export const buildAdminV2GrowthFormsEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2GrowthForms: builder.query<
    AdminV2GrowthFormsListResponse,
    {
      q?: string;
      status?: AdminV2GrowthFormStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/growth/forms",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2GrowthForms", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2FormFieldLibrary: builder.query<
    AdminV2FormFieldLibraryResponse,
    { organisationId?: string | null } | void
  >({
    query: (params) => ({
      url: "/admin/v2/forms/field-library",
      params: optionalQueryParams(params),
    }),
    providesTags: (result) =>
      result
        ? [
            { type: "AdminV2FormFieldLibrary", id: "LIST" },
            ...result.fields.map((field) => ({
              type: "AdminV2FormFieldLibrary" as const,
              id: field.id,
            })),
          ]
        : [{ type: "AdminV2FormFieldLibrary", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  createAdminV2FormFieldLibraryCustomField: builder.mutation<
    AdminV2FormFieldLibraryMutationResponse,
    {
      organisationId?: string | null;
      key: string;
      label: string;
      type: AdminV2GrowthFormFieldType;
      required: boolean;
      options: string[];
      helpText?: string | null;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/forms/field-library/custom-fields",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2FormFieldLibrary", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2FormFieldLibraryCustomField: builder.mutation<
    AdminV2FormFieldLibraryMutationResponse,
    {
      fieldId: string;
      body: {
        organisationId?: string | null;
        key?: string;
        label?: string;
        type?: AdminV2GrowthFormFieldType;
        required?: boolean;
        options?: string[];
        helpText?: string | null;
      };
    }
  >({
    query: ({ fieldId, body }) => ({
      url: `/admin/v2/forms/field-library/custom-fields/${fieldId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { fieldId }) => [
      { type: "AdminV2FormFieldLibrary", id: "LIST" },
      { type: "AdminV2FormFieldLibrary", id: fieldId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2FormFieldLibraryCustomField: builder.mutation<
    AdminV2FormFieldLibraryMutationResponse,
    {
      fieldId: string;
      organisationId?: string | null;
    }
  >({
    query: ({ fieldId, organisationId }) => ({
      url: `/admin/v2/forms/field-library/custom-fields/${fieldId}/archive`,
      method: "POST",
      data: organisationId === undefined ? {} : { organisationId },
    }),
    invalidatesTags: (_result, _error, { fieldId }) => [
      { type: "AdminV2FormFieldLibrary", id: "LIST" },
      { type: "AdminV2FormFieldLibrary", id: fieldId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  createAdminV2GrowthForm: builder.mutation<
    AdminV2GrowthFormDetailResponse,
    {
      name: string;
      organisationId?: string | null;
      slug?: string;
      description?: string | null;
      schema?: AdminV2JsonValue;
      settings?: AdminV2JsonValue;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/forms",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthForms", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2GrowthForm: builder.query<
    AdminV2GrowthFormDetailResponse,
    { formId: string; readOwner?: string }
  >({
    query: ({ formId }) => ({
      url: `/admin/v2/growth/forms/${formId}`,
    }),
    providesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: formId },
    ],
    keepUnusedDataFor: 20,
  }),

  updateAdminV2GrowthForm: builder.mutation<
    AdminV2GrowthFormDetailResponse,
    {
      formId: string;
      body: {
        name?: string;
        slug?: string;
        description?: string | null;
        schema?: AdminV2JsonValue;
        settings?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ formId, body }) => ({
      url: `/admin/v2/growth/forms/${formId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: "LIST" },
      { type: "AdminV2GrowthForms", id: formId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  duplicateAdminV2GrowthForm: builder.mutation<
    AdminV2GrowthFormDetailResponse,
    { formId: string; organisationId?: string | null }
  >({
    query: ({ formId, organisationId }) => ({
      url: `/admin/v2/growth/forms/${formId}/duplicate`,
      method: "POST",
      data: organisationId ? { organisationId } : {},
    }),
    invalidatesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: "LIST" },
      { type: "AdminV2GrowthForms", id: formId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2GrowthForm: builder.mutation<
    AdminV2GrowthFormDetailResponse,
    { formId: string }
  >({
    query: ({ formId }) => ({
      url: `/admin/v2/growth/forms/${formId}/publish`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: "LIST" },
      { type: "AdminV2GrowthForms", id: formId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  unpublishAdminV2GrowthForm: builder.mutation<
    AdminV2GrowthFormDetailResponse,
    { formId: string }
  >({
    query: ({ formId }) => ({
      url: `/admin/v2/growth/forms/${formId}/unpublish`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: "LIST" },
      { type: "AdminV2GrowthForms", id: formId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2GrowthForm: builder.mutation<
    AdminV2GrowthFormDetailResponse,
    { formId: string; reason: string }
  >({
    query: ({ formId, reason }) => ({
      url: `/admin/v2/growth/forms/${formId}/archive`,
      method: "POST",
      data: { reason },
    }),
    invalidatesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: "LIST" },
      { type: "AdminV2GrowthForms", id: formId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
